import {isObject, isString} from 'lodash';
import React, {FC, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {usePrevious} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  buildCancelTokenSource,
  CancelError,
} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {checkIsSalesforceConnected} from '../../../../helpers/integrationHelpers';
import {ResolveDispatchProps, useAppDispatch} from '../../../../helpers/reduxHelpers';
import {CrmSalesforceFieldModel} from '../../../../models/api/crmSalesforceFieldModel';
import {CrmSalesforceMetadataModel} from '../../../../models/api/crmSalesforceMetadataModel';
import {CrmSyncIntegrationResourceTypesEnum, CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {HttpResponseError} from '../../../../services/http/httpError';
import {getCompanyHasCrmSyncFeature} from '../../../../store/data/company/companySelectors';
import {
  disableCrmSyncAsyncAction,
  fetchOrCreateCrmSyncResourceAsyncAction,
  fetchSalesforceFieldMetadataAsyncAction,
  fetchSalesforceFieldsAsyncAction,
} from '../../../../store/data/crmSync/crmSyncsActions';
import {
  makeGetActiveSyncJob,
  makeGetCrmAccountSyncViaSource,
} from '../../../../store/data/crmSync/crmSyncsSelectors';
import {getCustomFieldsForAccountsFlat} from '../../../../store/data/customFields/customFieldsSelectors';
import {getSalesforceIntegration} from '../../../../store/data/integrations/integrationSelectors';
import {getIsUserGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {SalesforceCrmSyncDialog, SalesforceCrmSyncDialogProps} from './salesforceCrmSyncDialog';

/*
 * Props.
 */

type SalesforceCrmSyncDialogContainerProps = Omit<SalesforceCrmSyncDialogProps, 'hasExpiredCredentials'> &
  ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

/*
 * Selectors.
 */

export function makeGetIsConnected() {
  return createSelector([getSalesforceIntegration], checkIsSalesforceConnected);
}

export function makeGetSalesforceUsername() {
  return createSelector([getSalesforceIntegration], (salesforceIntegration) => {
    if (!salesforceIntegration) {
      return undefined;
    }
    return salesforceIntegration.settings.readonly?.user?.name;
  });
}

/*
 * Component.
 */

const SalesforceCrmSyncDialogContainerComponent: FC<SalesforceCrmSyncDialogContainerProps> = (props) => {
  const {
    panelId,
    crmSync,
    isConnected,
    isAdmin,
    activeSyncJob,
    customFields,
    shouldStartOnErrorsPage,
    username,
    onRequestClose,
    fetchOrCreateCrmSyncResourceAsync,
    disableCrmSyncAsync,
    hasCrmSyncFeature,
  } = props;

  const [hasTriedFetching, setHasTriedFetching] = useState(false);
  const [salesforceFields, setSalesforceFields] = useState<ReadonlyArray<CrmSalesforceFieldModel>>();
  const [salesforceMetadata, setSalesforceMetadata] = useState<CrmSalesforceMetadataModel>();
  const [hasExpiredCredentials, setHasExpiredCredentials] = useState(false);
  const [cancelToken] = useState(buildCancelTokenSource());

  const previousIsConnected = usePrevious(isConnected);
  const dispatch = useAppDispatch();

  // Only show a loading state if we dont have a crmSync object and we have not tried looking for one.
  // If we tried looking for one and still do not have one, that means we do not have the integration connected.
  const isLoading = !crmSync && !hasTriedFetching;

  // Fetches or creates a new crm sync object.
  useInitialEffect(() => {
    const checkOrCreateAsync = async () => {
      try {
        await fetchOrCreateCrmSyncResourceAsync(
          CrmSyncSourcesEnum.SALESFORCE,
          CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
          {
            cancelToken: cancelToken.token,
          },
        );
        setHasTriedFetching(true);
      } catch (error) {
        // Do nothing with errors from here.
      }
    };
    checkOrCreateAsync();

    return () => cancelToken.cancel();
  });

  useEffect(() => {
    // If we are not connected, just set the value as an empty array
    if (previousIsConnected === false && isConnected === false) {
      setSalesforceFields([]);
    }
    // If we just connected, we should reset all the values.
    if (previousIsConnected === false && isConnected) {
      setSalesforceFields(undefined);
      setHasExpiredCredentials(false);
    }
  }, [isConnected, previousIsConnected]);

  useEffect(() => {
    const fetchMappingAsync = async () => {
      try {
        const fieldMapping = await dispatch(
          fetchSalesforceFieldMetadataAsyncAction(
            CrmSyncSourcesEnum.SALESFORCE,
            CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
            {
              cancelToken: cancelToken.token,
            },
          ),
        );
        setSalesforceMetadata(fieldMapping);
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }
        if (
          error instanceof HttpResponseError &&
          isObject(error.body) &&
          'message' in error.body &&
          isString(error.body.message) &&
          error.body.message.includes('Invalid Salesforce Credentials')
        ) {
          setHasExpiredCredentials(true);
        }
      }
    };

    // If we do not have a salesforce object or we already have a mapping, return early.
    if (!crmSync?.id || salesforceMetadata || hasExpiredCredentials || !isConnected) {
      return;
    }
    fetchMappingAsync();
  }, [crmSync?.id, dispatch, salesforceMetadata, hasExpiredCredentials, cancelToken.token, isConnected]);

  // Fetch the salesforce fields and mapping available.
  useEffect(() => {
    const fetchFieldsAsync = async () => {
      try {
        const fields = await dispatch(
          fetchSalesforceFieldsAsyncAction(
            CrmSyncSourcesEnum.SALESFORCE,
            CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
            {
              cancelToken: cancelToken.token,
            },
          ),
        );
        setSalesforceFields((crmFields) => {
          if (fields && fields.length > 0) {
            return fields;
          }
          return crmFields ?? [];
        });
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }
        if (
          error instanceof HttpResponseError &&
          isObject(error.body) &&
          'message' in error.body &&
          isString(error.body.message) &&
          error.body.message.includes('Invalid Salesforce Credentials')
        ) {
          setHasExpiredCredentials(true);
        }
      }
    };

    // If we do not have a salesforce object or we already have the fields, return early.
    if (
      !crmSync?.id ||
      (salesforceFields && salesforceFields.length > 0) ||
      hasExpiredCredentials ||
      !isConnected
    ) {
      return;
    }
    fetchFieldsAsync();
  }, [crmSync?.id, dispatch, salesforceFields, hasExpiredCredentials, cancelToken.token, isConnected]);

  return (
    <SalesforceCrmSyncDialog
      panelId={panelId}
      crmSync={crmSync}
      isLoading={isLoading}
      isConnected={isConnected}
      isAdmin={isAdmin}
      salesforceFields={salesforceFields}
      salesforceMetadata={salesforceMetadata}
      activeSyncJob={activeSyncJob}
      hasExpiredCredentials={hasExpiredCredentials}
      customFields={customFields}
      shouldStartOnErrorsPage={shouldStartOnErrorsPage}
      username={username}
      onRequestClose={onRequestClose}
      disableCrmSyncAsync={disableCrmSyncAsync}
      hasCrmSyncFeature={hasCrmSyncFeature}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => {
  const getCrmSync = makeGetCrmAccountSyncViaSource(CrmSyncSourcesEnum.SALESFORCE);
  const getUsername = makeGetSalesforceUsername();
  return {
    crmSync: getCrmSync,
    isConnected: makeGetIsConnected(),
    isAdmin: getIsUserGlobalAdmin,
    activeSyncJob: makeGetActiveSyncJob(getCrmSync),
    customFields: getCustomFieldsForAccountsFlat,
    hasCrmSyncFeature: getCompanyHasCrmSyncFeature,
    username: getUsername,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  fetchOrCreateCrmSyncResourceAsync: fetchOrCreateCrmSyncResourceAsyncAction,
  disableCrmSyncAsync: disableCrmSyncAsyncAction,
}));

export const SalesforceCrmSyncDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SalesforceCrmSyncDialogContainerComponent);
